import {
  LocalInventoryFilters,
  FilterDisplayOptions,
  DEFAULT_FILTER_DISPLAY_OPTION,
} from '@/types';

const MIN_MAX_TO_CONDITION_KEYS: [string, string][] = [
  ['quantityMin', 'quantityMax'],
  ['quantityT1Min', 'quantityT1Max'],
  ['quantityT2Min', 'quantityT2Max'],
  ['quantityTxMin', 'quantityTxMax'],
  ['leverageRateMin', 'leverageRateMax'],
  ['valueEURMin', 'valueEURMax'],
  ['currencyBenchmarkMin', 'currencyBenchmarkMax'],
  ['dailyPotentialMin', 'dailyPotentialMax'],
  ['adtv30DMin', 'adtv30DMax'],
  ['adtvMultipleMin', 'adtvMultipleMax'],
  ['adtvAdjustedMin', 'adtvAdjustedMax'],
  ['benchmarkFeeMin', 'benchmarkFeeMax'],
  ['activeUtilisationMin', 'activeUtilisationMax'],
  ['quantityOnLoanMin', 'quantityOnLoanMax'],
  ['rebateRateMin', 'rebateRateMax'],
  ['l2cpQuantityMin', 'l2cpQuantityMax'],
  ['l2cpAmountEurMin', 'l2cpAmountEurMax'],
  ['l2cpRateMin', 'l2cpRateMax'],
  ['l2cpGrossRateMin', 'l2cpGrossRateMax'],
  ['l2cpSnpDeltaMin', 'l2cpSnpDeltaMax'],
  ['pendingTransfersOutMin', 'pendingTransfersOutMax'],
  ['cimbEq2QuantityMin', 'cimbEq2QuantityMax'],
  ['cimbSfQuantityMin', 'cimbSfQuantityMax'],
  ['cimbSf3QuantityMin', 'cimbSf3QuantityMax'],
  ['velocityLQuantityMin', 'velocityLQuantityMax'],
  ['velocityMQuantityMin', 'velocityMQuantityMax'],
];

const MIN_MAX_TO_CONDITION_KEY: Record<string, keyof LocalInventoryFilters> = {
  quantityMin: 'quantityCondition',
  quantityT1Min: 'quantityT1Condition',
  quantityT2Min: 'quantityT2Condition',
  quantityTxMin: 'quantityTxCondition',
  leverageRateMin: 'leverageRateCondition',
  valueEURMin: 'valueEURCondition',
  currencyBenchmarkMin: 'currencyBenchmarkCondition',
  dailyPotentialMin: 'dailyPotentialCondition',
  adtv30DMin: 'adtv30DCondition',
  adtvMultipleMin: 'adtvMultipleCondition',
  adtvAdjustedMin: 'adtvAdjustedCondition',
  benchmarkFeeMin: 'benchmarkFeeCondition',
  activeUtilisationMin: 'activeUtilisationCondition',
  quantityOnLoanMin: 'quantityOnLoanCondition',
  rebateRateMin: 'rebateRateCondition',
  l2cpQuantityMin: 'l2cpQuantityCondition',
  l2cpAmountEurMin: 'l2cpAmountEurCondition',
  l2cpRateMin: 'l2cpRateCondition',
  l2cpGrossRateMin: 'l2cpGrossRateCondition',
  l2cpSnpDeltaMin: 'l2cpSnpDeltaCondition',
  pendingTransfersOutMin: 'pendingTransfersOutCondition',
  cimbEq2QuantityMin: 'cimbEq2QuantityCondition',
  cimbSfQuantityMin: 'cimbSfQuantityCondition',
  cimbSf3QuantityMin: 'cimbSf3QuantityCondition',
  velocityLQuantityMin: 'velocityLQuantityCondition',
  velocityMQuantityMin: 'velocityMQuantityCondition',
};

function migrateMinMaxToCondition(
  persisted: Record<string, unknown>
): Partial<LocalInventoryFilters> {
  const out: Record<string, unknown> = {};
  for (const [minKey, maxKey] of MIN_MAX_TO_CONDITION_KEYS) {
    const conditionKey = MIN_MAX_TO_CONDITION_KEY[minKey];
    if (!conditionKey) continue;
    const minVal = persisted[minKey];
    const maxVal = persisted[maxKey];
    const hasOld = minVal !== undefined && minVal !== null || maxVal !== undefined && maxVal !== null;
    if (hasOld && (typeof minVal === 'number' || minVal === null) && (typeof maxVal === 'number' || maxVal === null)) {
      out[conditionKey] = {
        operator: 'between' as const,
        value1: typeof minVal === 'number' ? minVal : null,
        value2: typeof maxVal === 'number' ? maxVal : null,
      };
    }
  }
  if (persisted.accountIds) out.accountIds = persisted.accountIds;
  if (persisted.counterparties) out.counterparties = persisted.counterparties;
  if (persisted.currencies) out.currencies = persisted.currencies;
  if (persisted.caStatuses) out.caStatuses = persisted.caStatuses;
  if (persisted.caTypes) out.caTypes = persisted.caTypes;
  if (persisted.primaryAccountNames) out.primaryAccountNames = persisted.primaryAccountNames;
  if (persisted.primarySettlementCps) out.primarySettlementCps = persisted.primarySettlementCps;
  if (persisted.l2cpCounterparties) out.l2cpCounterparties = persisted.l2cpCounterparties;
  if (persisted.exDateFrom !== undefined) out.exDateFrom = persisted.exDateFrom;
  if (persisted.exDateTo !== undefined) out.exDateTo = persisted.exDateTo;
  for (const key of Object.keys(persisted)) {
    if (key.endsWith('Condition') && persisted[key] !== undefined) {
      out[key] = persisted[key];
    }
  }
  return out as Partial<LocalInventoryFilters>;
}

export function mergePersistedFilters(
  persisted: Partial<LocalInventoryFilters> & Record<string, unknown> | undefined,
  current: LocalInventoryFilters,
  defaults: LocalInventoryFilters
): LocalInventoryFilters {
  if (!persisted || typeof persisted !== 'object') return current;
  const hasOldShape = 'quantityMin' in persisted || 'quantityMax' in persisted;
  const source = hasOldShape ? migrateMinMaxToCondition(persisted as Record<string, unknown>) : persisted;
  const merged = { ...defaults };
  for (const key of Object.keys(merged) as (keyof LocalInventoryFilters)[]) {
    if (key in source && source[key] !== undefined) {
      (merged as Record<string, unknown>)[key] = source[key];
    }
  }
  return merged;
}

export function mergePersistedDisplayOptions(
  persisted: FilterDisplayOptions | undefined,
  current: FilterDisplayOptions,
  defaults: FilterDisplayOptions
): FilterDisplayOptions {
  if (!persisted || typeof persisted !== 'object') return current;
  const merged = { ...defaults };
  for (const [id, opt] of Object.entries(persisted)) {
    if (opt && typeof opt === 'object' && ('hideZero' in opt || 'hideBlanks' in opt)) {
      merged[id] = { ...DEFAULT_FILTER_DISPLAY_OPTION, ...opt };
    }
  }
  return merged;
}
